import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './configuration-create-panel.css';

/**
 * ConfigurationCreatePanel — slide-in панель создания конфигурации
 * По макету Figma. Overlay + правая панель 480 px.
 *
 * Заменяет Ant Design Modal для создания.
 */

// ==================== SVG ICONS ====================

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InfoIcon = ({ title }) => (
  <svg
    className="cfgp__info-icon"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-label={title}
  >
    <title>{title}</title>
    <circle cx="6" cy="6" r="5.25" stroke="currentColor" strokeWidth="1" />
    <path d="M6 5.5V8.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    <circle cx="6" cy="3.75" r="0.6" fill="currentColor" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M3.5 7.5L6 5L8.5 7.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M3.5 4.5L6 7L8.5 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ==================== DEFAULTS ====================

const DEFAULTS = {
  name: '',
  description: '',
  slimiter: 5,
  rlimiter: 0,
  maxconn: 200,
  priority_level: 5,
  is_limiter: true,
  one_proxy_per_port: true,
  max_per_client: 20,
  min_order_quantity: 0,
  max_order_quantity: 50,
  default_expires_days: 30,
  allow_custom_expires: true,
  auto_renewal: true,
  modem_settings_enabled: false,
  sellable_in_cabinet: true,
  base_price: 0,
  price_per_day: 0,
  price_per_7_days: 0,
};

// ==================== SUB-COMPONENTS ====================

const NumberField = ({ label, value, onChange, onKeyDown, min, max, step = 1, infoTitle, ariaLabel }) => {
  const handleIncrement = () => {
    const num = Number(value) || 0;
    const next = Math.min(num + step, max ?? Infinity);
    onChange(next);
  };

  const handleDecrement = () => {
    const num = Number(value) || 0;
    const next = Math.max(num - step, min ?? -Infinity);
    onChange(next);
  };

  return (
    <div className="cfgp__field">
      <div className="cfgp__label">
        <span className="cfgp__label-text">{label}</span>
        {infoTitle && <InfoIcon title={infoTitle} />}
      </div>
      <div className="cfgp__number-wrap">
        <input
          type="number"
          className="cfgp__input cfgp__input--number"
          value={value}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') { onChange(''); return; }
            let num = Number(raw);
            if (isNaN(num)) return;
            if (min !== undefined && num < min) num = min;
            if (max !== undefined && num > max) num = max;
            onChange(num);
          }}
          onKeyDown={onKeyDown}
          min={min}
          max={max}
          step={step}
          aria-label={ariaLabel || label}
        />
        <div className="cfgp__number-arrows">
          <button
            type="button"
            className="cfgp__number-arrow cfgp__number-arrow--up"
            onClick={handleIncrement}
            tabIndex={-1}
            aria-label="Увеличить"
          >
            <ChevronUpIcon />
          </button>
          <button
            type="button"
            className="cfgp__number-arrow cfgp__number-arrow--down"
            onClick={handleDecrement}
            tabIndex={-1}
            aria-label="Уменьшить"
          >
            <ChevronDownIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

const ToggleRow = ({ label, checked, onChange, infoTitle }) => (
  <div className="cfgp__toggle-row">
    <span className="cfgp__toggle-label">{label}</span>
    <button
      type="button"
      className={`cfgp__toggle${checked ? ' cfgp__toggle--on' : ''}`}
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      tabIndex={0}
    >
      <span className="cfgp__toggle-dot" />
    </button>
    {infoTitle && <InfoIcon title={infoTitle} />}
  </div>
);

// ==================== MAIN COMPONENT ====================

const ConfigurationCreatePanel = ({
  visible = false,
  onClose = null,
  onSubmit = null,
  submitting = false,
}) => {
  const [formData, setFormData] = useState({ ...DEFAULTS });
  const [errors, setErrors] = useState({});

  // ==================== RESET ON OPEN ====================
  useEffect(() => {
    if (visible) {
      setFormData({ ...DEFAULTS });
      setErrors({});
    }
  }, [visible]);

  // ==================== HANDLERS ====================
  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const validate = useCallback(() => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Введите название конфигурации';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData.name]);

  const handleSubmit = useCallback(() => {
    if (!validate()) return;
    if (!onSubmit) return;

    const values = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      slimiter: Number(formData.slimiter) || 0,
      rlimiter: Number(formData.rlimiter) || 0,
      maxconn: Number(formData.maxconn) || 0,
      priority_level: Number(formData.priority_level) || 5,
      is_limiter: formData.is_limiter,
      one_proxy_per_port: formData.one_proxy_per_port,
      max_per_client: Number(formData.max_per_client) || 20,
      min_order_quantity: Number(formData.min_order_quantity) || 0,
      max_order_quantity: Number(formData.max_order_quantity) || 50,
      default_expires_days: Number(formData.default_expires_days) || 30,
      allow_custom_expires: formData.allow_custom_expires,
      auto_renewal: formData.auto_renewal,
      modem_settings_enabled: formData.modem_settings_enabled,
      sellable_in_cabinet: formData.sellable_in_cabinet !== false,
      base_price: Math.max(Number(formData.base_price) || 0, 0),
      price_per_day: Math.max(Number(formData.price_per_day) || 0, 0),
      price_per_7_days: Math.max(Number(formData.price_per_7_days) || 0, 0),
    };

    onSubmit(values);
  }, [formData, validate, onSubmit]);

  const handleClose = useCallback(() => {
    if (!submitting && onClose) onClose();
  }, [submitting, onClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  // ==================== EARLY RETURN ====================
  if (!visible) return null;

  const panelElement = (
    <div
      className="cfgp-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="cfgp" role="dialog" aria-label="Создание конфигурации">

        {/* ==================== HEADER ==================== */}
        <div className="cfgp__header">
          <h2 className="cfgp__title">создание конфигурации</h2>
          <button
            type="button"
            className="cfgp__close-btn"
            onClick={handleClose}
            aria-label="Закрыть"
            tabIndex={0}
          >
            <CloseIcon />
          </button>
        </div>

        {/* ==================== BODY ==================== */}
        <div className="cfgp__body">

          {/* --- Section: Основные настройки --- */}
          <div className="cfgp__section">
            <div className="cfgp__section-title">Основные настройки</div>

            {/* Row: Название + Описание */}
            <div className="cfgp__row">
              <div className="cfgp__field">
                <div className="cfgp__label">
                  <span className="cfgp__label-required">*</span>
                  <span className="cfgp__label-text">Название</span>
                </div>
                <input
                  type="text"
                  className={`cfgp__input${errors.name ? ' cfgp__input--error' : ''}`}
                  placeholder="Введите название"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  onKeyDown={handleKeyDown}
                  aria-label="Название конфигурации"
                  aria-required="true"
                  autoComplete="off"
                  autoFocus
                />
                {errors.name && <span className="cfgp__field-error">{errors.name}</span>}
              </div>
              <div className="cfgp__field">
                <div className="cfgp__label">
                  <span className="cfgp__label-text">Описание</span>
                </div>
                <input
                  type="text"
                  className="cfgp__input"
                  placeholder="Введите описание"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  onKeyDown={handleKeyDown}
                  aria-label="Описание"
                  autoComplete="off"
                />
              </div>
            </div>

            {/* Row: Скорость + Запросы */}
            <div className="cfgp__row">
              <NumberField
                label="Скорость (Мб/сек)"
                value={formData.slimiter}
                onChange={(v) => handleChange('slimiter', v)}
                onKeyDown={handleKeyDown}
                min={0}
                max={1000}
                step={0.1}
                infoTitle="Ограничение скорости в Мб/сек. 0 = без ограничений"
              />
              <NumberField
                label="Запросы (RPS)"
                value={formData.rlimiter}
                onChange={(v) => handleChange('rlimiter', v)}
                onKeyDown={handleKeyDown}
                min={0}
                max={100000}
                infoTitle="Максимальное количество запросов в секунду. 0 = без ограничений"
              />
            </div>

            {/* Row: Соединения + Приоритет */}
            <div className="cfgp__row">
              <NumberField
                label="Соединения"
                value={formData.maxconn}
                onChange={(v) => handleChange('maxconn', v)}
                onKeyDown={handleKeyDown}
                min={0}
                max={10000}
                infoTitle="Максимальное количество TCP соединений. 0 = без ограничений"
              />
              <NumberField
                label="Приоритет"
                value={formData.priority_level}
                onChange={(v) => handleChange('priority_level', v)}
                onKeyDown={handleKeyDown}
                min={1}
                max={10}
              />
            </div>

            {/* Toggles */}
            <ToggleRow
              label="Лимитер скорости"
              checked={formData.is_limiter}
              onChange={(v) => handleChange('is_limiter', v)}
              infoTitle="Включает/выключает лимитер скорости в Kraken"
            />
            <ToggleRow
              label="1 прокси на порт"
              checked={formData.one_proxy_per_port}
              onChange={(v) => handleChange('one_proxy_per_port', v)}
              infoTitle="Если включено, клиент не сможет получить несколько прокси на одном порту"
            />
          </div>

          {/* --- Section: Ограничения --- */}
          <div className="cfgp__section cfgp__section--grow">
            <div className="cfgp__section-title">Ограничения</div>

            {/* Row: Макс. прокси + Мин. заказа */}
            <div className="cfgp__row">
              <NumberField
                label="Макс. прокси на клиента"
                value={formData.max_per_client}
                onChange={(v) => handleChange('max_per_client', v)}
                onKeyDown={handleKeyDown}
                min={1}
                max={500}
              />
              <NumberField
                label="Мин. количество заказа"
                value={formData.min_order_quantity}
                onChange={(v) => handleChange('min_order_quantity', v)}
                onKeyDown={handleKeyDown}
                min={0}
                max={200}
              />
            </div>

            {/* Row: Макс. заказа + Срок действия */}
            <div className="cfgp__row">
              <NumberField
                label="Макс. количество заказа"
                value={formData.max_order_quantity}
                onChange={(v) => handleChange('max_order_quantity', v)}
                onKeyDown={handleKeyDown}
                min={1}
                max={200}
              />
              <NumberField
                label="Срок действия (дней)"
                value={formData.default_expires_days}
                onChange={(v) => handleChange('default_expires_days', v)}
                onKeyDown={handleKeyDown}
                min={1}
                max={365}
              />
            </div>

            {/* Toggles */}
            <ToggleRow
              label="Разрешить настройку срока"
              checked={formData.allow_custom_expires}
              onChange={(v) => handleChange('allow_custom_expires', v)}
            />
            <ToggleRow
              label="Автопродление"
              checked={formData.auto_renewal}
              onChange={(v) => handleChange('auto_renewal', v)}
            />
            <ToggleRow
              label="Отображать настройки pOF"
              checked={formData.modem_settings_enabled}
              onChange={(v) => handleChange('modem_settings_enabled', v)}
              infoTitle="При покупке и редактировании прокси в личном кабинете клиенту будут доступны pOF, ротация и интервал смены IP"
            />
            <ToggleRow
              label="Продавать в личном кабинете"
              checked={formData.sellable_in_cabinet !== false}
              onChange={(v) => handleChange('sellable_in_cabinet', v)}
              infoTitle="Выключено — тариф скрыт из каталога ЛК и недоступен для quote/create/pay (служебные тарифы)"
            />
          </div>

          <div className="cfgp__section">
            <div className="cfgp__section-title">Конфигурации</div>
            <div className="cfgp__row">
              <NumberField
                label="Стоимость (₽ / 1 день)"
                value={formData.price_per_day}
                onChange={(v) => handleChange('price_per_day', v)}
                onKeyDown={handleKeyDown}
                min={0}
                max={1000000}
                step={1}
                infoTitle="Цена за одну единицу (прокси) на 1 день. Если 0 — для срока 1 день считается от стоимости за период ниже"
                ariaLabel="Стоимость за 1 день, рублей"
              />
              <NumberField
                label="Стоимость (₽ / 7 дней)"
                value={formData.price_per_7_days}
                onChange={(v) => handleChange('price_per_7_days', v)}
                onKeyDown={handleKeyDown}
                min={0}
                max={1000000}
                step={1}
                infoTitle="Цена за одну единицу на 7 дней. Если 0 — для срока 7 дней считается от стоимости за период ниже"
                ariaLabel="Стоимость за 7 дней, рублей"
              />
              <NumberField
                label="Стоимость (₽ / месяц)"
                value={formData.base_price}
                onChange={(v) => handleChange('base_price', v)}
                onKeyDown={handleKeyDown}
                min={0}
                max={1000000}
                step={1}
                infoTitle="Базовая стоимость за период «срок действия (дней)» в блоке Ограничения; для других сроков сумма пропорциональна, если не заданы цены за 1 или 7 дней"
                ariaLabel="Стоимость за период по умолчанию, рублей в месяц"
              />
            </div>
          </div>
        </div>

        {/* ==================== FOOTER ==================== */}
        <div className="cfgp__footer">
          <button
            type="button"
            className="cfgp__btn cfgp__btn--cancel"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Отмена"
            tabIndex={0}
          >
            Отмена
          </button>
          <button
            type="button"
            className="cfgp__btn cfgp__btn--submit"
            onClick={handleSubmit}
            disabled={submitting}
            aria-label="Создать"
            tabIndex={0}
          >
            {submitting ? 'Создание...' : 'Создать'}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined') {
    return createPortal(panelElement, document.body);
  }

  return panelElement;
};

export default ConfigurationCreatePanel;
